/**
 * Chip selection, as a pure reducer.
 *
 * Tipping Pitches (and Discerning Eye) require TWO chip guesses. The Lock In
 * gate and the caption both knew that; the tile row did not. Clicking a second,
 * different tile fell through to "select slot 1" and overwrote the first pick,
 * so the two-chip requirement could never be satisfied — Lock In stayed disabled
 * and the at-bat could not be submitted at all. The half-inning could only
 * continue by deactivating the card server-side.
 *
 * The generalisable guard the bug report asked for: the number of chips the UI
 * ALLOWS must equal the number it REQUIRES. Both now come from `required`, and
 * `isSelectionComplete` is what the Lock In gate reads, so the two cannot drift.
 *
 * Second, quieter bug fixed here: the old handler let one chip occupy BOTH slots
 * ("click your selected chip to double it up"). The resolver only promotes the
 * second guess when `chip2 != chip` (server.py:5846), so doubling up silently
 * forfeited the card's whole benefit while looking like a valid selection.
 */

/** Chips the seat currently has selected, oldest first. */
export const selectedChips = ({ chip1 = null, chip2 = null }) =>
  [chip1, chip2].filter(c => c != null);

/** True when the seat may submit — this is the Lock In gate. */
export const isSelectionComplete = (sel, required = 1) =>
  selectedChips(sel).length >= required;

/**
 * Apply a click on chip `n`.
 *
 * @param sel      {chip1, chip2}
 * @param n        the chip clicked (1-4)
 * @param required how many chips this at-bat needs (1, or 2 under Tipping
 *                 Pitches / Discerning Eye)
 * @returns a new {chip1, chip2}
 */
export function nextChipSelection(sel, n, required = 1) {
  const chip1 = sel?.chip1 ?? null;
  const chip2 = sel?.chip2 ?? null;

  if (required < 2) {
    // Single-chip mode: plain toggle. chip2 is never used, and is cleared so a
    // stale second pick cannot ride along if the card is deactivated mid-at-bat.
    return { chip1: chip1 === n ? null : n, chip2: null };
  }

  // Clicking an already-selected chip deselects that slot. Deselecting slot 1
  // pulls slot 2 down, so the slots stay packed and "first empty slot" below is
  // always meaningful.
  if (chip1 === n) return { chip1: chip2, chip2: null };
  if (chip2 === n) return { chip1, chip2: null };

  // A different chip: fill the first empty slot. The missing branch here is what
  // caused the deadlock — a different chip with slot 1 full used to overwrite
  // slot 1 instead of filling slot 2.
  if (chip1 === null) return { chip1: n, chip2: null };
  if (chip2 === null) return { chip1, chip2: n };

  // Both slots full: drop the oldest, so a third click always changes something
  // rather than appearing inert.
  return { chip1: chip2, chip2: n };
}

/** Which slot a chip occupies, for display. */
export const slotOf = (sel, n) =>
  sel?.chip1 === n ? "1" : sel?.chip2 === n ? "2" : null;

/**
 * Caption for the chip row: "Curveball", or "Curveball + 1 more" while a
 * required pick is still outstanding.
 */
export function selectionCaption(sel, required, nameOf) {
  const chosen = selectedChips(sel).map(nameOf);
  if (!chosen.length) return required > 1 ? `Select ${required} pitch types` : "Select a pitch type";
  const missing = required - chosen.length;
  return missing > 0 ? `${chosen.join(", ")} + ${missing} more` : chosen.join(", ");
}
